import EventEmitter from 'eventemitter3';
import { useContext, useState, useRef, useCallback, useEffect, createContext } from 'react';
import { createPortal } from 'react-dom';
import { jsx, Fragment } from 'react/jsx-runtime';

class ToastCancelled extends Error {
  constructor() {
    super("Toast cancelled by host");
    this.name = "ToastCancelled";
  }
}
function isCancelledByHost(e) {
  return e instanceof ToastCancelled;
}
const UpdateSignal = Symbol("UpdateSignal");
function defer() {
  let settled = false;
  let rs = () => {};
  let rj = () => {};
  const promise = new Promise((resolve, reject) => {
    rs = value => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    rj = reason => {
      if (!settled) {
        settled = true;
        reject(reason);
      }
    };
  });
  return {
    resolve: rs,
    reject: rj,
    promise,
    get settled() {
      return settled;
    }
  };
}
const DisposeDeferralsContext = /*#__PURE__*/createContext(void 0);
function useDisposeEffect(fn, deps = []) {
  const deferrals = useContext(DisposeDeferralsContext);
  const [isDisposing, setIsDisposing] = useState(false);
  const fnRef = useRef(fn);
  if (fnRef.current !== fn) {
    fnRef.current = fn;
  }
  const finish = deferrals?.disposeFinish.resolve;
  const callback = useCallback(() => {
    if (!finish) {
      return;
    }
    if (fnRef.current) {
      return fnRef.current(finish);
    }
    finish();
  }, [finish, ...deps]);
  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        await deferrals?.disposeBegin.promise;
        if (mounted) {
          setIsDisposing(true);
        }
      } catch (_) {}
    })();
    return () => {
      mounted = false;
    };
  }, [deferrals]);
  useEffect(() => {
    if (isDisposing) {
      return callback();
    }
  }, [isDisposing, callback]);
}
let lastToastableKey = 0;
function toaster() {
  const emitter = new EventEmitter();
  const items = /* @__PURE__ */new Map();
  function set(key, metadata) {
    const item = items.get(key);
    if (item) {
      items.set(key, {
        ...item,
        ...metadata
      });
    } else {
      const deferrals = {
        disposeBegin: defer(),
        disposeFinish: defer()
      };
      items.set(key, {
        ...metadata,
        deferrals
      });
      void (async () => {
        try {
          await Promise.allSettled([deferrals.disposeBegin.promise, deferrals.disposeFinish.promise]);
          items.delete(key);
          emitter.emit("update");
        } catch (_) {}
      })();
    }
    emitter.emit("update");
  }
  function dispose(key) {
    items.get(key)?.deferrals.disposeBegin.resolve();
  }
  return {
    /**
     * React component that renders all active toasts.
     * Can render inline or inside a portal.
     */
    Container({
      inline = false,
      ...containerProps
    }) {
      const itemsRef = useRef(items);
      const emitterRef = useRef(emitter);
      const [container, setContainer] = useState();
      const [entries, setEntries] = useState([]);
      useEffect(() => {
        function onUpdate() {
          setEntries([...itemsRef.current.entries()]);
        }
        onUpdate();
        const {
          current: emitter2
        } = emitterRef;
        emitter2.on("update", onUpdate);
        return () => {
          emitter2.off("update", onUpdate);
        };
      }, []);
      useEffect(() => {
        if (inline) {
          setContainer(void 0);
          return () => {};
        }
        const div = document.createElement("div");
        div.className = "hi_from_toasterhea";
        document.body.appendChild(div);
        setContainer(div);
        return () => {
          document.body.removeChild(div);
        };
      }, [inline]);
      if (inline) {
        return /*#__PURE__*/jsx("div", {
          ...containerProps,
          children: entries.map(([{
            value: key
          }, {
            component: C,
            props,
            deferrals
          }]) => /*#__PURE__*/jsx(DisposeDeferralsContext.Provider, {
            value: deferrals,
            children: /*#__PURE__*/jsx(C, {
              ...props
            })
          }, key))
        });
      }
      if (!container) {
        return /*#__PURE__*/jsx(Fragment, {});
      }
      return /*#__PURE__*/createPortal( /*#__PURE__*/jsx("div", {
        ...containerProps,
        children: entries.map(([{
          value: key
        }, {
          component: C,
          props,
          deferrals
        }]) => /*#__PURE__*/jsx(DisposeDeferralsContext.Provider, {
          value: deferrals,
          children: /*#__PURE__*/jsx(C, {
            ...props
          })
        }, key))
      }), container);
    },
    /**
     * Returns whether any toast is currently active (optionally filtered by component).
     */
    hasActive(component) {
      for (const item of items.values()) {
        if (component != null && item.component !== component) {
          continue;
        }
        if (!item.deferrals.disposeBegin.settled) {
          return true;
        }
      }
      return false;
    },
    on(eventName, listener) {
      emitter.on(eventName, listener);
    },
    off(eventName, listener) {
      emitter.off(eventName, listener);
    },
    /**
     * Wraps a component into a toastable interface: `pop()` to show, `discard()` to cancel.
     */
    create(component) {
      let deferral;
      let key;
      async function pop(...args) {
        const [props = {}] = args;
        deferral?.reject(UpdateSignal);
        deferral = defer();
        if (!key) {
          lastToastableKey += 1;
          key = {
            value: lastToastableKey
          };
        }
        set(key, {
          component,
          props: {
            ...props,
            resolve: deferral.resolve,
            reject: deferral.reject
          }
        });
        try {
          while (1) {
            try {
              return await deferral.promise;
            } catch (e) {
              if (e !== UpdateSignal) {
                throw e;
              }
            }
          }
        } finally {
          dispose(key);
          key = void 0;
        }
        throw "The impossible happened\u2026 Quick, make a wish!";
      }
      function discard() {
        deferral?.reject(new ToastCancelled());
      }
      return {
        pop,
        discard
      };
    }
  };
}

export { ToastCancelled, defer, isCancelledByHost, toaster, useDisposeEffect };
